"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // Base styles
        "z-50 overflow-hidden rounded-md px-3 py-1.5",
        // Colors - dark background with light text
        "bg-foreground text-background",
        // Typography
        "text-xs font-medium",
        // Shadow
        "shadow-elevated",
        // Animation
        "animate-in fade-in-0 zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * Simple tooltip wrapper for icon buttons
 * Provides consistent tooltip styling with keyboard shortcut support
 *
 * @example
 * <IconTooltip content="Bold" shortcut="⌘B">
 *   <Button variant="ghost" size="icon">
 *     <Bold className="w-4 h-4" />
 *   </Button>
 * </IconTooltip>
 */
interface IconTooltipProps {
  children: React.ReactNode;
  content: string;
  shortcut?: string;
  side?: "top" | "right" | "bottom" | "left";
  delayDuration?: number;
}

/**
 * IconTooltip - Simple tooltip wrapper for icon buttons
 *
 * WORKAROUND for React 19 + Radix compose-refs infinite loop:
 * Use asChild on a wrapper span (not the button itself). The span is a simple
 * element without ref callbacks, so compose-refs works correctly. The button
 * inside the span handles all click events normally.
 * See: https://github.com/radix-ui/primitives/issues/3799
 *
 * Accessibility (WCAG 4.1.2 Name, Role, Value): a Radix tooltip is wired up
 * with aria-describedby and is not rendered at all while closed, so it never
 * gives the trigger an accessible name. An icon-only button wrapped here would
 * be announced as just "button". Lend the tooltip text to the child as an
 * aria-label — but only when the child is genuinely icon-only.
 *
 * The "only" matters: overriding a control that already shows a word would
 * break WCAG 2.5.3 Label in Name, because the spoken name would no longer
 * contain the visible text (the zoom button reads "100%" but its tooltip says
 * "Custom Zoom"). So a child that renders any text of its own, or that already
 * carries aria-label / aria-labelledby / title, is left exactly as it is.
 */
function hasOwnLabel(props: Record<string, unknown>) {
  return Boolean(props["aria-label"] || props["aria-labelledby"] || props.title);
}

/** True when the subtree renders any visible text, so the control names itself. */
function rendersText(node: React.ReactNode, depth = 0): boolean {
  if (node === null || node === undefined || typeof node === "boolean") return false;
  if (typeof node === "string") return node.trim().length > 0;
  if (typeof node === "number") return true;
  if (depth > 6) return false;
  if (Array.isArray(node)) return node.some((child) => rendersText(child, depth + 1));
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode; "aria-hidden"?: boolean | string };
    if (props["aria-hidden"] === true || props["aria-hidden"] === "true") return false;
    return rendersText(props.children, depth + 1);
  }
  return false;
}

function IconTooltip({
  children,
  content,
  shortcut,
  side = "top",
  delayDuration = 300,
}: IconTooltipProps) {
  const needsName =
    React.isValidElement(children) &&
    !hasOwnLabel(children.props as Record<string, unknown>) &&
    !rendersText((children.props as { children?: React.ReactNode }).children);
  const labelled = needsName
    ? React.cloneElement(children as React.ReactElement<{ "aria-label"?: string }>, { "aria-label": content })
    : children;

  return (
    <Tooltip delayDuration={delayDuration}>
      {/* Use asChild on span wrapper to avoid nested buttons and compose-refs issues */}
      <TooltipTrigger asChild>
        <span className="inline-flex">{labelled}</span>
      </TooltipTrigger>
      <TooltipContent side={side}>
        <span className="flex items-center gap-2">
          {content}
          {shortcut && (
            <kbd className="inline-flex items-center justify-center rounded bg-white/20 dark:bg-black/20 px-1.5 py-0.5 text-[10px] font-mono">
              {shortcut}
            </kbd>
          )}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Tooltip with arrow pointer
 */
const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    className={cn("fill-foreground", className)}
    {...props}
  />
));
TooltipArrow.displayName = "TooltipArrow";

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipArrow,
  IconTooltip,
};
